import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { transactionAPI } from '../services/api';
import { Search, Plus, Edit, Trash2, MessageSquare } from 'lucide-react';
import { formatDate } from '../utils/formatters';

const Consultations = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingConsultation, setEditingConsultation] = useState(null);
  
  const [formData, setFormData] = useState({
    member_number: '',
    customer_name: '',
    type: '',
    content: '',
    consultation_date: '',
    staff_name: ''
  });

  // 상담 목록 조회
  const { data: consultations, isLoading } = useQuery({
    queryKey: ['consultations', currentPage, searchTerm, selectedType],
    queryFn: () => transactionAPI.getConsultations({
      page: currentPage,
      limit: 10,
      search: searchTerm,
      type: selectedType
    }),
    keepPreviousData: true,
    select: (data) => {
      // API 응답 구조에 따라 안전하게 처리
      if (data?.data?.consultations) {
        return {
          data: data.data.consultations,
          pagination: data.data.pagination
        };
      } else if (data?.data) {
        return {
          data: Array.isArray(data.data) ? data.data : [],
          pagination: data.pagination
        };
      } else if (Array.isArray(data)) {
        return {
          data: data,
          pagination: null
        };
      }
      return {
        data: [],
        pagination: null
      };
    }
  });

  // 상담 추가/수정 뮤테이션
  const mutation = useMutation({
    mutationFn: (data) => {
      if (editingConsultation) {
        return transactionAPI.updateConsultation(editingConsultation.id, data);
      } else {
        return transactionAPI.createConsultation(data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['consultations']);
      showToast(editingConsultation ? '상담이 수정되었습니다.' : '상담이 등록되었습니다.', 'success');
      setShowAddModal(false);
      setEditingConsultation(null);
      resetForm();
    },
    onError: (error) => {
      showToast(error.message || '오류가 발생했습니다.', 'error');
    }
  });

  const resetForm = () => {
    setFormData({
      member_number: '',
      customer_name: '',
      type: '',
      content: '',
      consultation_date: '',
      staff_name: ''
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate(formData);
  };

  const handleEdit = (consultation) => {
    setEditingConsultation(consultation);
    setFormData({
      member_number: consultation.member_number,
      customer_name: consultation.customer_name,
      type: consultation.type,
      content: consultation.content,
      consultation_date: consultation.consultation_date,
      staff_name: consultation.staff_name
    });
    setShowAddModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      transactionAPI.deleteConsultation(id).then(() => {
        queryClient.invalidateQueries(['consultations']);
        showToast('상담이 삭제되었습니다.', 'success');
      }).catch((error) => {
        showToast(error.message || '삭제 중 오류가 발생했습니다.', 'error');
      });
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case '일반상담':
        return 'status-progress';
      case '계약상담':
        return 'status-waiting';
      case '문의':
        return 'status-completed';
      default:
        return 'status-cancelled';
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">상담 관리</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary"
        >
          <Plus size={20} />
          상담 등록
        </button>
      </div>

      {/* 검색 및 필터 */}
      <div className="card mb-6">
        <div className="grid grid-3" style={{ gap: '20px' }}>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1">
              검색
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="묘적번호, 상담자명으로 검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input w-full pl-10"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1">
              상담 유형
            </label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="select w-full"
            >
              <option value="">전체 유형</option>
              <option value="일반상담">일반상담</option>
              <option value="계약상담">계약상담</option>
              <option value="문의">문의</option>
              <option value="기타">기타</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setShowAddModal(true)}
              className="btn btn-primary w-full"
            >
              <Plus size={20} />
              상담 등록
            </button>
          </div>
        </div>
      </div>

      {/* 상담 목록 테이블 */}
      <div className="card">
        {isLoading ? (
          <div className="loading">로딩 중...</div>
        ) : consultations?.data?.length === 0 ? (
          <div className="empty-state">
            <MessageSquare className="mx-auto h-12 w-12 text-gray-300 mb-4" />
            <p className="text-gray-500">상담 내역이 없습니다.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead>
                  <tr>
                    <th>상담일</th>
                    <th>묘적번호</th>
                    <th>상담자명</th>
                    <th>상담 유형</th>
                    <th>상담 내용</th>
                    <th>담당자</th>
                    <th>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {consultations?.data?.map((consultation) => (
                    <tr key={consultation.id}>
                      <td>{formatDate(consultation.consultation_date)}</td>
                      <td className="font-medium">{consultation.member_number}</td>
                      <td>{consultation.customer_name}</td>
                      <td>
                        <span className={`status-badge ${getTypeColor(consultation.type)}`}>
                          {consultation.type}
                        </span>
                      </td>
                      <td>{consultation.content}</td>
                      <td>{consultation.staff_name}</td>
                      <td>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(consultation)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(consultation.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 페이지네이션 */}
            {consultations?.pagination && (
              <div className="flex justify-between items-center mt-6">
                <div className="text-sm text-gray-700">
                  총 {consultations.pagination.total}개 중 {consultations.pagination.from}-{consultations.pagination.to}개
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="btn btn-secondary"
                  >
                    이전
                  </button>
                  <span className="px-3 py-2 text-sm text-gray-700">
                    {currentPage} / {consultations.pagination.last_page}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    disabled={currentPage >= consultations.pagination.last_page}
                    className="btn btn-secondary"
                  >
                    다음
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* 상담 등록/수정 모달 */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2 className="text-xl font-bold mb-4">
              {editingConsultation ? '상담 수정' : '상담 등록'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    묘적번호
                  </label>
                  <input
                    type="text"
                    value={formData.member_number}
                    onChange={(e) => setFormData({...formData, member_number: e.target.value})}
                    className="input w-full"
                    placeholder="묘적번호를 입력하세요"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    상담자명
                  </label>
                  <input
                    type="text"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                    className="input w-full"
                    placeholder="상담자명을 입력하세요"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    상담 유형
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="select w-full"
                    required
                  >
                    <option value="">상담 유형을 선택하세요</option>
                    <option value="일반상담">일반상담</option>
                    <option value="계약상담">계약상담</option>
                    <option value="문의">문의</option>
                    <option value="기타">기타</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    상담일
                  </label>
                  <input
                    type="date"
                    value={formData.consultation_date}
                    onChange={(e) => setFormData({...formData, consultation_date: e.target.value})}
                    className="input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    상담 내용
                  </label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                    className="input w-full"
                    rows="4"
                    placeholder="상담 내용을 입력하세요"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    담당자
                  </label>
                  <input
                    type="text"
                    value={formData.staff_name}
                    onChange={(e) => setFormData({...formData, staff_name: e.target.value})}
                    className="input w-full"
                    placeholder="담당자명을 입력하세요"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <button
                  type="submit"
                  className="btn btn-primary flex-1"
                  disabled={mutation.isLoading}
                >
                  {mutation.isLoading ? '저장 중...' : (editingConsultation ? '수정' : '등록')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingConsultation(null);
                    resetForm();
                  }}
                  className="btn btn-secondary flex-1"
                >
                  취소
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Consultations; 